import {
  BuildingIcon,
  CaretDownIcon,
  CheckIcon,
  CopyIcon,
  GearIcon,
  PencilSimpleIcon,
  PlusIcon,
  SpinnerIcon,
  XIcon,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import type { OrgSummary } from '@/hooks/use-org';
import { cn } from '@/lib/utils';
import { syncActiveOrg, updateOrgServerFn } from '@/server/functions/organization';
import { CreateOrgDialog } from './create-org-dialog';

interface OrgSwitcherProps {
  currentOrg: OrgSummary;
  orgs: OrgSummary[];
  gitBaseUrl: string;
  onOrgUpdated?: () => void;
}

interface CopyButtonProps {
  value: string;
  className?: string;
}

function CopyButton({ value, className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const CopyStatusIcon = copied ? CheckIcon : CopyIcon;

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn('p-1 rounded hover:bg-mesa-cream-dark transition-colors', className)}
      title="Copy to clipboard"
    >
      <CopyStatusIcon
        weight={copied ? 'bold' : 'regular'}
        className={cn('size-4', copied ? 'text-green-600' : 'text-mesa-muted')}
      />
    </button>
  );
}

interface OrgInfoRowProps {
  label: string;
  value: string;
  showCopy?: boolean;
  onEdit?: () => void;
}

function OrgInfoRow({ label, value, showCopy = true, onEdit }: OrgInfoRowProps) {
  return (
    <div className="px-3 py-1.5">
      <div className="text-xs font-medium text-mesa-muted mb-0.5">{label}</div>
      <div className="flex items-center gap-1.5">
        {showCopy && <CopyButton value={value} />}
        <span className="text-sm break-all">{value}</span>
        {onEdit && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="p-1 rounded hover:bg-mesa-cream-dark transition-colors ml-auto"
            title="Edit"
          >
            <PencilSimpleIcon className="size-4 text-mesa-muted" />
          </button>
        )}
      </div>
    </div>
  );
}

export function OrgSwitcher({ currentOrg, orgs, gitBaseUrl, onOrgUpdated }: OrgSwitcherProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { isCollapsed } = useSidebar();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  const baseUrlWithoutProtocol = useMemo(() => gitBaseUrl.replace(/^https?:\/\//, ''), [gitBaseUrl]);
  const orgUrl = `${baseUrlWithoutProtocol}/${currentOrg.slug}`;

  const handleSwitch = async (targetOrg: OrgSummary) => {
    if (targetOrg.id === currentOrg.id || isSwitching) return;
    setIsSwitching(true);
    setError(null);
    try {
      await syncActiveOrg({ data: { orgId: targetOrg.id } });
      onOrgUpdated?.();
      await router.navigate({
        to: '/$orgSlug/repositories',
        params: { orgSlug: targetOrg.slug },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch organization');
    } finally {
      setIsSwitching(false);
    }
  };

  const handleStartEdit = () => {
    setEditName(currentOrg.name);
    setRenameError(null);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditName('');
    setRenameError(null);
  };

  const handleSaveEdit = async () => {
    if (!editName.trim() || editName.trim() === currentOrg.name) {
      handleCancelEdit();
      return;
    }

    setIsRenaming(true);
    setRenameError(null);

    try {
      await updateOrgServerFn({ data: { orgSlug: currentOrg.slug, name: editName.trim() } });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['auth-orgs'] }),
        queryClient.invalidateQueries({ queryKey: ['auth-org'] }),
      ]);
      setIsEditing(false);
      setEditName('');
    } catch (err) {
      setRenameError(err instanceof Error ? err.message : 'Failed to rename');
    } finally {
      setIsRenaming(false);
    }
  };

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <div className="flex aspect-square size-8 shrink-0 items-center justify-center bg-mesa-green text-white">
                  <BuildingIcon weight="fill" />
                </div>
                {!isCollapsed && (
                  <>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-medium">{currentOrg.name}</span>
                      <span className="truncate text-xs text-mesa-muted">Organization</span>
                    </div>
                    <CaretDownIcon className="ml-auto" />
                  </>
                )}
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[--sidebar-width] min-w-64" align="start" sideOffset={4}>
              <div className="border-b border-mesa-border pb-2 mb-1">
                {isEditing ? (
                  <div className="px-3 py-2">
                    <span className="text-xs font-medium text-mesa-muted mb-1 block">Display Name</span>
                    <div className="flex items-center gap-2">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') handleSaveEdit();
                          if (e.key === 'Escape') handleCancelEdit();
                        }}
                        className="h-8 text-sm"
                        autoFocus
                        disabled={isRenaming}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2"
                        onClick={handleSaveEdit}
                        disabled={isRenaming || !editName.trim()}
                      >
                        {isRenaming ? (
                          <SpinnerIcon className="size-4 animate-spin" weight="fill" />
                        ) : (
                          <CheckIcon className="size-4 text-green-600" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2"
                        onClick={handleCancelEdit}
                        disabled={isRenaming}
                      >
                        <XIcon className="size-4" />
                      </Button>
                    </div>
                    {renameError && <p className="text-xs text-mesa-red mt-1">{renameError}</p>}
                  </div>
                ) : (
                  <OrgInfoRow label="Display Name" value={currentOrg.name} onEdit={handleStartEdit} />
                )}

                <OrgInfoRow label="URL" value={orgUrl} />
                <OrgInfoRow label="Organization ID" value={currentOrg.id} />
              </div>

              <DropdownMenuLabel className="text-xs">Switch Organization</DropdownMenuLabel>
              {error && <div className="px-2 py-1.5 text-sm text-mesa-red">{error}</div>}
              <div className="max-h-[40vh] overflow-y-auto">
                {orgs.map((targetOrg) => (
                  <DropdownMenuItem key={targetOrg.id} onClick={() => handleSwitch(targetOrg)} disabled={isSwitching}>
                    <BuildingIcon className="mr-2" />
                    <span className="truncate">{targetOrg.name}</span>
                    {targetOrg.id === currentOrg.id && <CheckIcon className="ml-auto" size={12} weight="bold" />}
                  </DropdownMenuItem>
                ))}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setIsCreateOpen(true)}>
                <PlusIcon className="mr-2" />
                Create organization
              </DropdownMenuItem>
              <a
                href={`/${currentOrg.slug}/settings`}
                className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-sm outline-none transition-colors hover:bg-mesa-cream-dark"
              >
                <GearIcon className="mr-2" />
                Organization settings
              </a>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      <CreateOrgDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        gitBaseUrl={gitBaseUrl}
        onOrgCreated={onOrgUpdated}
      />
    </>
  );
}
